import { MESSAGE_LIMITS } from './messaging.js';

/**
 * Aufbewahrungs- und Löschfristen (DSGVO: Speicherbegrenzung). **Einzige Quelle**: Der nächtliche Aufräumlauf
 * (`apps/api/src/maintenance/cleanup.ts`) und die Datenschutzerklärung (`apps/web/src/pages/legal/`) lesen
 * von hier, damit Text und Verhalten nie auseinanderlaufen. Feste Werte, bewusst kurz. Eine Änderung hier ändert
 * Aufräumlauf und Text zugleich; sie gehört vorher mit dem Datenschutz abgestimmt.
 */
export const RETENTION = {
  /** Versendete oder endgültig fehlgeschlagene E-Mails (enthalten die Empfängeradresse, auch die Käuferadresse des Kaufbelegs; die Grenzen je Adresse zählen darüber). */
  outboxDays: 30,
  /** Abgelaufene oder verbrauchte Einmal-Token, Refresh-Token und Einladungen. */
  tokensDays: 30,
  /** Fehlversuche beim Anmelden. */
  attemptsDays: 1,
  /** Konten, die ihre E-Mail-Adresse nie bestätigt haben (gerechnet ab dem Anlegen und ab dem letzten Bestätigungslink). */
  unverifiedAccountHours: 48,
  /** Abgelaufene oder widerrufene Basar-Sitzungen der Kassen. */
  eventSessionsDays: 30,
  /** Namen und Gerätebezeichnungen der Kassen mit Schnellzugang (Plan 06, E11) werden nach Basar-Ende so viele Tage zu „Gerät N“. */
  deviceNameDays: 30,
  /** IP-Adressen im Audit-Log werden danach geleert (der Eintrag selbst bleibt). */
  auditIpDays: 90,
  /** Abgelaufene oder widerrufene Support-Zugriffe (Plan Nr. 27). Der Audit-Log-Eintrag bleibt unabhängig davon bestehen. */
  supportGrantsDays: 30,
  /** Personendaten der Helferschicht-Eintragungen (Plan Nr. 41): nach Basar-Ende (closes_at, sonst ends_at ...) so viele Tage. */
  shiftSignupDays: 30,
  /** Begründung und Antwort von Schicht-Anfragen (Plan Nr. 13): so viele Tage nach Schichtende, dann leer. Die Zeile folgt mit `shiftSignupDays`. */
  shiftRequestTextDays: 14,
  /** Konten ohne Anmeldung seit so vielen Jahren werden anonymisiert (Plan Nr. 36, DSGVO Speicherbegrenzung). */
  inactiveAccountYears: 3,
  /** Tageszähler der Foto-Erkennung (C7); nur der laufende Tag zählt, ältere Zeilen haben keine Funktion mehr. */
  recognitionUsageDays: 7,
  /** Zeitpunkte der letzten Hinweis-Mail je Person, Basar und Art (nur für die Drosselung von 15 Minuten nötig). */
  notificationThrottleDays: 2,
  /**
   * Nachrichten, Rundschreiben und Neuigkeiten eines Basars werden so viele Tage nach dessen Ende (`ends_at`)
   * gelöscht (Plan Nr. 40, gleiche sechs Monate wie bei den Kontaktdaten). Entscheidung des Auftraggebers (04.10.):
   * 180 Tage bleiben; die 90 Tage gelten nur für Rundschreiben der Organisation.
   * Basare ohne Ende bleiben stehen.
   */
  messagingDaysAfterEventEnd: 180,
  /**
   * Rundschreiben der Organisation (Plan Nr. 11) werden so viele Tage nach dem Senden gelöscht (es gibt kein Basar-Ende;
   * Entscheidung des Auftraggebers: 90 Tage).
   */
  orgBroadcastDays: MESSAGE_LIMITS.orgBroadcastRetentionDays,
  /** Versandmarken der Erinnerungen aus dem Ablaufplan (Plan 08, X3): so viele Tage nach dem Ende des Basars. */
  scheduleReminderDaysAfterEventEnd: 90,
  /** Versandmarken der Einladungen früherer Verkäufer (X1) werden so viele Monate nach der Einladung gelöscht. */
  sellerInviteMonths: 24,
  /**
   * Kommentare der Kassen („Problem melden“, U17f) werden so viele Tage nach dem Ende des Basars gelöscht (wie die Nachrichten),
   * ebenso die freien Texte der Kasse an Verkaufszeilen (Storno, Preiskorrektur, Artikel ohne Etikett, U17d/g/h).
   */
  registerNoteDaysAfterEventEnd: 180,
  /** Fotos zu Artikeln ohne Etikett (U17h) werden so viele Tage nach dem Abrechnen des Basars gelöscht ... */
  salePhotoDaysAfterSettlement: 30,
  /** ... spätestens aber so viele Tage nach der Aufnahme ... */
  salePhotoMaxDays: 90,
  /** ... und Fotos, auf die keine Verkaufszeile verweist, nach so vielen Stunden. */
  salePhotoOrphanHours: 24,
  /** Eigene Basar-Vorlagen im Papierkorb (Plan 16, U16b) werden so viele Tage nach dem Löschen endgültig entfernt. */
  eventTemplateTrashDays: 30,
  /** Erledigte oder ausgelassene Pläne für den Versand der Vorab-Mail (U16d, nur Zeitpunkt und Zahl): so viele Tage nach dem Lauf. */
  inviteScheduleDays: 90,
  /**
   * Bankverbindung für Auszahlungen (W3-Auszahlung): wird so viele Monate nach der letzten Nutzung (GiroCode oder SEPA-Datei),
   * sonst nach dem Eintragen gelöscht. Jederzeit vorher selbst löschbar, mit dem Konto sofort weg. Offene Überweisungswahlen
   * fallen dabei auf bar zurück. Prüfung durch den Anwalt (Entscheidung 9).
   */
  bankAccountMonthsAfterLastUse: 12,
  /**
   * Dauer-Etiketten je Person (Auftrag C2, `seller_label_codes`): Ein Code, der seit so vielen Monaten weder zugeordnet (gerechnet
   * ab der letzten Zuordnung, sonst ab dem Anlegen) noch, wenn gesperrt, seit der Sperre benutzt wurde, wird gesperrt und von der
   * Person gelöst (kein Personenbezug mehr). Die Zeile selbst bleibt als Grabstein stehen: Ein Code wird nie neu vergeben. Mit dem
   * Konto sofort gesperrt. Erst nach der Datenschutzprüfung (Hauptschalter) relevant.
   */
  poolLabelMonthsAfterLastUse: 24,
  /** Mitteilungen im Ausgang (Push, Plan 17 W3-Push-a): nur Titel, Text und Pfad. Versendete und fehlgeschlagene entfallen nach so vielen Tagen. */
  pushOutboxDays: 7,
  /** Geräte für Mitteilungen ohne angenommene Mitteilung seit so vielen Tagen werden gelöscht (der Browser meldet Abmeldungen oft nicht). */
  pushSubscriptionIdleDays: 180,
  /** Planermarken der Mitteilungen (nur Zeitpunkte je Anmeldung) werden so viele Tage nach dem Ende des Basars gelöscht. */
  pushMarkDaysAfterEventEnd: 30,
  /** Einlass-Pässe (W3-Bonus-b, Token, erster Einlass, Zahl der Prüfungen): so viele Tage nach dem Ende des Basars gelöscht, mit dem Konto sofort. */
  entryPassDaysAfterEventEnd: 30,
  /** Digitale Unterschriften (Bild, Zeitpunkt, Art): so viele Tage nach dem Ende des Basars gelöscht, mit dem Konto sofort. */
  signatureDaysAfterEventEnd: 180,
  /** Artikelbilder (Welle 4g, verkleinerte Fassung): so viele Tage nach dem Ende des Basars gelöscht, mit dem Konto, mit dem Löschen des Artikels und auf Wunsch des Verkäufers (Entfernen) sofort. */
  itemImageDaysAfterEventEnd: 180,
} as const;

export type RetentionKey = keyof typeof RETENTION;
